/**
 * Auto-fill module for traveller forms.
 * Automatically populates travel/accommodation details from selected offers.
 */

import { setHiddenValue, formatDateForInput } from '../../lib/helpers';
import type { OfferDetails, SelectedOfferData } from '../../lib/types';

/**
 * Get all selected offer IDs across all groups
 */
export function getAllSelectedOfferIds(
    accommodationSelect: HTMLSelectElement,
    transportSelect: HTMLSelectElement,
    insuranceSelect: HTMLSelectElement
): string[] {
    const ids: string[] = [];
    Array.from(accommodationSelect.selectedOptions).forEach(o => ids.push(o.value));
    Array.from(transportSelect.selectedOptions).forEach(o => ids.push(o.value));
    Array.from(insuranceSelect.selectedOptions).forEach(o => ids.push(o.value));
    return ids;
}

/**
 * Extract aggregated offer data from selected offers.
 * @param getAgencyName optional getter for the current agency display name
 */
export function getSelectedOfferData(
    selectedIds: string[],
    offerDetailsCache: Record<string, OfferDetails>,
    getAgencyName?: () => string,
    root: ParentNode = document
): SelectedOfferData | null {
    if (selectedIds.length === 0) return null;

    const offer = offerDetailsCache[selectedIds[0]];
    if (!offer) return null;

    let minFrom: string | null = null;
    let maxTo: string | null = null;

    // Sub-range date pickers (Accommodation, Insurance)
    root.querySelectorAll<HTMLInputElement>('.sub-range-from-picker').forEach(fp => {
        const from = fp.value;
        const unitId = fp.dataset.unitId;
        const to = unitId
            ? root.querySelector<HTMLInputElement>(`.sub-range-to-picker[data-unit-id="${unitId}"]`)?.value || ''
            : '';
        if (from && (!minFrom || from < minFrom)) minFrom = from;
        if (to   && (!maxTo   || to   > maxTo  )) maxTo   = to;
    });

    // Transport price checkboxes
    root.querySelectorAll<HTMLInputElement>('[name$=".SelectedOfferPriceIds"]:checked').forEach(cb => {
        const from = cb.getAttribute('data-from') || '';
        const to   = cb.getAttribute('data-to')   || '';
        if (from && (!minFrom || from < minFrom)) minFrom = from;
        if (to   && (!maxTo   || to   > maxTo  )) maxTo   = to;
    });

    const firstUnit = offer.offerUnits?.[0];
    const unitName = firstUnit?.name || '';
    const serviceClassification = firstUnit?.service || firstUnit?.classification || '';

    let serviceDays = 0;
    if (minFrom && maxTo) {
        const diffMs = new Date(maxTo).getTime() - new Date(minFrom).getTime();
        serviceDays = Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
    }

    return {
        entryPlace: offer.entryPlace || '',
        transportNumber: offer.transportNumber || '',
        offerName: offer.name || '',
        unitName,
        city: offer.city || '',
        address: offer.address || '',
        fromDate: minFrom ? formatDateForInput(minFrom) : '',
        toDate: maxTo ? formatDateForInput(maxTo) : '',
        agencyName: getAgencyName ? getAgencyName() : '',
        serviceDays,
        serviceClassification,
    };
}

/**
 * Fill hidden traveller fields from selected offer data.
 * Pass null for data to clear all fields.
 */
export function fillTravellerFromOffer(prefix: string, data: SelectedOfferData | null) {
    setHiddenValue(`${prefix}.EntryPlace`, data?.entryPlace ?? '');
    setHiddenValue(`${prefix}.TransportNumber`, data?.transportNumber ?? '');
    setHiddenValue(`${prefix}.EntryDate`, data?.fromDate ?? '');
    setHiddenValue(`${prefix}.AccommodationUnit`, data ? (data.unitName || data.offerName) : '');
    setHiddenValue(`${prefix}.CheckInDate`, data?.fromDate ?? '');
    setHiddenValue(`${prefix}.CheckOutDate`, data?.toDate ?? '');
    setHiddenValue(`${prefix}.PlannedCheckOutDate`, data?.toDate ?? '');
    setHiddenValue(`${prefix}.SitNumber`, '');
    setHiddenValue(`${prefix}.ArrivalMethod`, '');
    setHiddenValue(`${prefix}.StayReason`, '');
    setHiddenValue(`${prefix}.AccommodationFloor`, '');
    setHiddenValue(`${prefix}.RoomNumber`, '');
    setHiddenValue(`${prefix}.TaxReductionReasonId`, '');
    setHiddenValue(`${prefix}.ServiceType`, '');
    setHiddenValue(`${prefix}.NumberOfServiceDays`, data ? String(data.serviceDays || '') : '');
    setHiddenValue(`${prefix}.VoucherBarcode`, '');
    setHiddenValue(`${prefix}.AgencyName`, data?.agencyName ?? '');
}

/**
 * Auto-fill all traveller forms (contract owner + all passengers)
 */
export function autoFillAllTravellers(
    selectedIds: string[],
    offerDetailsCache: Record<string, OfferDetails>,
    passengersContainer: HTMLElement,
    getAgencyName?: () => string
) {
    const data = getSelectedOfferData(selectedIds, offerDetailsCache, getAgencyName);
    fillTravellerFromOffer('ContractOwner', data);
    const cards = passengersContainer.querySelectorAll('.passenger-card');
    cards.forEach((_, idx) => fillTravellerFromOffer(`Travellers[${idx}]`, data));
}
